import * as authActions from '@payaca/store/auth/authActions';
import { startSubmit, stopSubmit } from 'redux-form';
import {
  all,
  call,
  delay,
  put,
  race,
  take,
  takeEvery,
} from 'redux-saga/effects';

import { parseAccountUsers, parseContactNumber } from '@/helpers/userHelper';

import { refreshAuthToken } from '@payaca/store/auth/refreshAuthToken';
import * as api from '../services/api';

const initialState = {
  myProfile: {},
  signUpDetails: {
    address: {},
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    contactNumber: '',
  },
  newBusinessAccount: {},
  newCustomerDetails: {
    name: '',
    email: '',
    contactNumber: '',
    firstLineAddress: '',
    secondLineAddress: '',
    city: '',
    postcode: '',
  },
  currentItem: {
    name: '',
    description: '',
    price: '',
    vatAmount: 20, // FIXME
    vatIncluded: true,
    cisDeductionRate: null,
    isReverseChargeVat: false,
    taxRateId: null,
  },
  customers: [],
  gettingCustomers: false,
  gettingProfile: false,
  gettingTerms: false,
  terms: [],
  globalSettings: {},
  xeroAccounts: [],
  subscriptionPrices: [],
  accountUsers: [],
  businessAttachments: [],
};

export function reducer(state = initialState, action) {
  switch (action.type) {
    case 'users.storeGlobalSettings': {
      return {
        ...state,
        globalSettings: action.globalSettings,
      };
    }

    case 'users.storeMyProfile': {
      return {
        ...state,
        myProfile: {
          ...action.profile,
          contactNumber: parseContactNumber(action.profile.contactNumber),
        },
        gettingProfile: false,
      };
    }

    case 'users.storeMyAccount': {
      const [_first, ...rest] = state.myProfile.accounts || [];
      return {
        ...state,
        myProfile: {
          ...state.myProfile,
          accounts: [action.payload, ...rest],
        },
      };
    }

    case 'users.clearMyProfile': {
      return { ...initialState };
    }

    case 'users.buildSignUpDetails': {
      return {
        ...state,
        signUpDetails: {
          ...state.signUpDetails,
          ...action.signUpDetails,
        },
      };
    }

    case 'users.clearSignUpDetails': {
      return {
        ...state,
        myProfile: {},
        signUpDetails: {
          address: {},
        },
        newBusinessAccount: {},
        newCustomerDetails: {
          address: {},
        },
      };
    }

    case 'users.buildNewBusinessAccount': {
      return {
        ...state,
        newBusinessAccount: {
          ...state.newBusinessAccount,
          ...action.newBusinessAccount,
        },
      };
    }

    case 'users.clearNewBusinessAccount': {
      return {
        ...state,
        newBusinessAccount: {},
      };
    }

    case 'users.storeNewAccountLogo': {
      return {
        ...state,
        newAccountLogo: action.image,
      };
    }

    case 'users.storeTerms': {
      return {
        ...state,
        terms: action.terms,
        gettingTerms: false,
      };
    }

    case 'users.storeBusinessAttachments': {
      return {
        ...state,
        businessAttachments: action.businessAttachments,
        gettingBusinessAttachments: false,
      };
    }

    case 'users.storeXeroAccounts': {
      return {
        ...state,
        xeroAccounts: action.xeroAccounts,
      };
    }

    case 'users.storeAccountUsers': {
      const parsedAccountUsers = parseAccountUsers([...action.accountUsers]);
      return {
        ...state,
        accountUsers: parsedAccountUsers,
        gettingUsers: false,
      };
    }

    case 'users.getProfile': {
      return {
        ...state,
        gettingProfile: true,
      };
    }

    case 'users.getAccountUsers': {
      return {
        ...state,
        gettingUsers: true,
      };
    }

    case 'users.storeSubscriptionPrices': {
      return { ...state, subscriptionPrices: action.data };
    }

    case 'users.getAccountTerms': {
      return {
        ...state,
        gettingTerms: false,
      };
    }

    case 'users.getBusinessAttachments': {
      return {
        ...state,
        gettingAttachments: false,
      };
    }

    default: {
      return state;
    }
  }
}

export const actions = {
  // getGlobalSettings: () => ({ type: 'users.getGlobalSettings' }),
  // storeGlobalSettings: globalSettings => ({ type: 'users.storeGlobalSettings', globalSettings }),
  // registerUserDevice: (deviceToken, callback) => ({ type: 'users.registerUserDevice', deviceToken, callback }),
  getProfile: (callback) => ({ type: 'users.getProfile', callback }),
  storeMyProfile: (profile) => ({ type: 'users.storeMyProfile', profile }),
  clearMyProfile: () => ({
    type: 'users.clearMyProfile',
  }),
  setAnalyticsUserId: (userId) => ({
    type: 'users.setAnalyticsUserId',
    userId,
  }),
  // updateNotificationSettings: (payload, callback) => ({ type: 'users.updateNotificationSettings', payload, callback }),
  // agreeToPrivacy: callback => ({ type: 'users.agreeToPrivacy', callback }),
  updatePassword: (payload, callback) => ({
    type: 'users.updatePassword',
    payload,
    callback,
  }),
  resendEmailVerification: (email, callback) => ({
    type: 'users.resendEmailVerification',
    email,
    callback,
  }),
  buildSignUpDetails: (signUpDetails) => ({
    type: 'users.buildSignUpDetails',
    signUpDetails,
  }),
  // clearSignUpDetails: () => ({ type: 'users.clearSignUpDetails' }),
  // buildNewBusinessAccount: newBusinessAccount => ({ type: 'users.buildNewBusinessAccount', newBusinessAccount }),
  // clearNewBusinessAccount: () => ({ type: 'users.clearNewBusinessAccount' }),
  createBusinessAccount: (businessAccount, callback) => ({
    type: 'users.createBusinessAccount',
    businessAccount,
    callback,
  }),
  updateBusinessAccount: (accountId, payload, callback) => ({
    type: 'users.updateBusinessAccount',
    accountId,
    payload,
    callback,
  }),
  validateEmail: (email, callback) => ({
    type: 'users.validateEmail',
    email,
    callback,
  }),
  validatePhone: (phone, callback) => ({
    type: 'users.validatePhone',
    phone,
    callback,
  }),
  // storeNewAccountLogo: image => ({ type: 'users.storeNewAccountLogo', image }),
  uploadAccountLogo: (accountId, imageFile, callback) => ({
    type: 'users.uploadAccountLogo',
    accountId,
    imageFile,
    callback,
  }),
  getAccountTerms: (accountId, callback) => ({
    type: 'users.getAccountTerms',
    accountId,
    callback,
  }),
  getBusinessAttachments: (callback) => ({
    type: 'users.getBusinessAttachments',
    callback,
  }),
  storeBusinessAttachments: (businessAttachments) => ({
    type: 'users.storeBusinessAttachments',
    businessAttachments,
  }),
  manageBusinessAttachments: (documentsToAdd, documentsToRemove, callback) => ({
    type: 'users.manageBusinessAttachments',
    documentsToAdd,
    documentsToRemove,
    callback,
  }),
  storeTerms: (terms) => ({
    type: 'users.storeTerms',
    terms,
  }),
  manageTermsDocuments: (
    accountId,
    documentsToAdd,
    documentsToRemove,
    callback
  ) => ({
    type: 'users.manageTermsDocuments',
    accountId,
    documentsToAdd,
    documentsToRemove,
    callback,
  }),
  uploadTermsDocument: (accountId, termsDocument, callback) => ({
    type: 'users.uploadTermsDocument',
    accountId,
    termsDocument,
    callback,
  }),
  removeTermsDocument: (accountId, termsDocumentId, callback) => ({
    type: 'users.removeTermsDocument',
    accountId,
    termsDocumentId,
    callback,
  }),
  clearItem: () => ({ type: 'users.clearItem' }),
  connectWithXero: (code, callback) => ({
    type: 'users.connectWithXero',
    code,
    callback,
  }),
  connectWithGoogle: (code, callback) => ({
    type: 'users.connectWithGoogle',
    code,
    callback,
  }),
  importXeroContacts: (callback) => ({
    type: 'users.importXeroContacts',
    callback,
  }),
  importXeroItems: (callback) => ({
    type: 'users.importXeroItems',
    callback,
  }),
  removeXeroConnection: (callback) => ({
    type: 'users.removeXeroConnection',
    callback,
  }),
  getXeroAccounts: (callback) => ({
    type: 'users.getXeroAccounts',
    callback,
  }),
  storeXeroAccounts: (xeroAccounts) => ({
    type: 'users.storeXeroAccounts',
    xeroAccounts,
  }),
  updateXeroSettings: (payload, callback) => ({
    type: 'users.updateXeroSettings',
    payload,
    callback,
  }),
  connectWithQuickbooks: (payload, callback) => ({
    type: 'users.connectWithQuickbooks',
    payload,
    callback,
  }),
  importQuickbooksCustomers: (callback) => ({
    type: 'users.importQuickbooksCustomers',
    callback,
  }),
  importQuickbooksItems: (callback) => ({
    type: 'users.importQuickbooksItems',
    callback,
  }),
  removeQuickbooksConnection: (callback) => ({
    type: 'users.removeQuickbooksConnection',
    callback,
  }),
  storeSubscriptionPrices: (data) => ({
    type: 'users.storeSubscriptionPrices',
    data,
  }),
  storeAccountUsers: (accountUsers) => ({
    type: 'users.storeAccountUsers',
    accountUsers,
  }),
  getAccountUsers: (callback) => ({
    type: 'users.getAccountUsers',
    callback,
  }),
  inviteUserToAccount: (payload, callback) => ({
    type: 'users.inviteUserToAccount',
    payload,
    callback,
  }),
  reactivateUser: (userId, callback) => ({
    type: 'users.reactivateUser',
    userId,
    callback,
  }),
  deleteUserFromAccount: (userId, callback) => ({
    type: 'users.deleteUserFromAccount',
    userId,
    callback,
  }),
  deleteUsersFromAccount: (userIds, callback) => ({
    type: 'users.deleteUsersFromAccount',
    userIds,
    callback,
  }),

  connectWithStripe: (code, callback) => ({
    type: 'users.connectWithStripe',
    code,
    callback,
  }),
  removeStripeConnection: (callback) => ({
    type: 'users.removeStripeConnection',
    callback,
  }),
  removeGoogleConnection: (callback) => ({
    type: 'users.removeGoogleConnection',
    callback,
  }),
};

const watchGetGlobalSettings = function* watchGetGlobalSettings() {
  while (true) {
    yield take('users.getGlobalSettings');
    try {
      const { response, timeout } = yield race({
        response: call(api.getGlobalSettings),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException('Get global settings timed out.');
      }
      yield put(actions.storeGlobalSettings(response));
    } catch (e) {
      console.log(e);
    }
  }
};

const watchRegisterUserDevice = function* watchRegisterUserDevice() {
  while (true) {
    const { deviceToken, callback } = yield take('users.registerUserDevice');
    try {
      const { response, timeout } = yield race({
        response: call(api.registerUserDevice, deviceToken),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException('Get profile request timed out.');
      }

      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      console.log(e);
      if (callback) {
        callback(e);
      }
    }
  }
};

const watchGetProfile = function* watchGetProfile() {
  while (true) {
    const { callback } = yield take('users.getProfile');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.getProfile),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException('Get profile request timed out.');
      }
      yield put(actions.storeMyProfile(response));

      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      console.log(e);
      if (callback) {
        callback(e);
      }
    }
  }
};

const watchUpdateNotificationSettings =
  function* watchUpdateNotificationSettings() {
    while (true) {
      const { payload, callback } = yield take(
        'users.updateNotificationSettings'
      );
      try {
        const { response, timeout } = yield race({
          response: call(api.updateNotificationSettings, payload),
          timeout: delay(15000),
        });
        if (timeout) {
          throw new api.TimeoutException('Update profile request timed out.');
        }
        yield put(actions.storeMyProfile(response));

        if (callback) {
          callback(null, response);
        }
      } catch (e) {
        console.log(e);
        if (callback) {
          callback(e);
        }
      }
    }
  };

const watchUpdatePassword = function* watchUpdatePassword() {
  while (true) {
    const { payload, callback } = yield take('users.updatePassword');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.updatePassword, payload),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException('Update password request timed out.');
      }

      yield put(authActions.storeTokens(response.token, response.refreshToken));
      yield take('auth.storeTokensSuccess');

      if (callback) {
        callback(null);
      }
    } catch (e) {
      console.log(e);
      if (callback) {
        callback(e);
      }
    }
  }
};

const watchResendEmailVerification = function* watchResendEmailVerification() {
  while (true) {
    const { email, callback } = yield take('users.resendEmailVerification');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.resendEmailVerification, email),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException(
          'Resend email verification request timed out.'
        );
      }

      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      console.log(e);
      if (callback) {
        callback(e);
      }
    }
  }
};

const watchValidateEmail = function* watchValidateEmail() {
  yield takeEvery('users.validateEmail', function* (action) {
    const email = action.email;
    const callback = action.callback;

    try {
      const { response, timeout } = yield race({
        response: call(api.validateEmail, email),
        timeout: delay(15000),
      });

      if (timeout) {
        throw new api.TimeoutException('Validate email request timed out.');
      }
      if (callback && response) {
        callback(null, response.isValid);
      }
    } catch (e) {
      console.log(e);
      if (callback) {
        callback(e);
      }
    }
  });
};

const watchValidatePhone = function* watchValidatePhone() {
  yield takeEvery('users.validatePhone', function* (action) {
    const phone = action.phone;
    const callback = action.callback;
    try {
      const { response, timeout } = yield race({
        response: call(api.validatePhone, phone),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException('Validate phone request timed out.');
      }

      if (callback && response) {
        callback(null, response.isValid);
      }
    } catch (e) {
      console.log(e);
      if (callback) {
        callback(e);
      }
    }
  });
};

const watchCreateBusinessAccount = function* watchCreateBusinessAccount() {
  while (true) {
    const { businessAccount, callback } = yield take(
      'users.createBusinessAccount'
    );
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.createBusinessAccount, businessAccount),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException(
          'Create business account request timed out.'
        );
      }

      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      console.log(e);
      if (callback) {
        callback(e);
      }
    }
  }
};

const watchUpdateBusinessAccount = function* watchUpdateBusinessAccount() {
  while (true) {
    const { accountId, payload, callback } = yield take(
      'users.updateBusinessAccount'
    );
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.updateBusinessAccount, accountId, payload),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException(
          'Update business account request timed out.'
        );
      }

      if (callback) {
        callback(null, response);
      }

      yield put({
        type: 'users.storeMyAccount',
        payload: response,
      });
    } catch (e) {
      console.log(e, e.message);
      if (callback) {
        callback(e);
      }
    }
  }
};

const watchUploadAccountLogo = function* watchUploadAccountLogo() {
  while (true) {
    const { accountId, imageFile, callback } = yield take(
      'users.uploadAccountLogo'
    );
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: imageFile
          ? call(api.uploadAccountLogo, accountId, imageFile)
          : call(api.removeAccountLogo),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException(
          'Upload account logo request timed out.'
        );
      }

      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      console.log(e);
      if (callback) {
        callback(e);
      }
    }
  }
};

const watchGetBusinessAttachments = function* watchGetBusinessAttachments() {
  while (true) {
    const { callback } = yield take('users.getBusinessAttachments');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.getBusinessAttachments),
        timeout: delay(60000),
      });
      if (timeout) {
        throw new api.TimeoutException(
          'Get business attachments request timed out.'
        );
      }
      yield put(actions.storeBusinessAttachments(response));
      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      console.log(e);
      if (callback) {
        callback(e);
      }
    }
  }
};

const watchGetAccountTerms = function* watchGetAccountTerms() {
  while (true) {
    const { accountId, callback } = yield take('users.getAccountTerms');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.getAccountTerms, accountId),
        timeout: delay(60000),
      });
      if (timeout) {
        throw new api.TimeoutException('Get account terms request timed out.');
      }
      yield put(actions.storeTerms(response));
      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      console.log(e);
      if (callback) {
        callback(e);
      }
    }
  }
};

const watchManageTermsDocuments = function* watchManageTermsDocuments() {
  while (true) {
    const { accountId, documentsToAdd, documentsToRemove, callback } =
      yield take('users.manageTermsDocuments');
    try {
      const { response, timeout } = yield race({
        response: call(
          api.manageTermsDocuments,
          accountId,
          documentsToAdd,
          documentsToRemove
        ),
        timeout: delay(60000),
      });
      yield call(refreshAuthToken);
      if (timeout) {
        throw new api.TimeoutException(
          'Manage of terms documents to account request timed out.'
        );
      }

      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      const message = JSON.parse(e.message);
      if (callback) {
        callback(message);
      }
    }
  }
};
const watchManageBusinessAttachments =
  function* watchManageBusinessAttachments() {
    while (true) {
      const { documentsToAdd, documentsToRemove, callback } = yield take(
        'users.manageBusinessAttachments'
      );
      yield call(refreshAuthToken);
      try {
        const { response, timeout } = yield race({
          response: call(
            api.manageBusinessAttachments,
            documentsToAdd,
            documentsToRemove
          ),
          timeout: delay(60000),
        });
        if (timeout) {
          throw new api.TimeoutException(
            'Manage of business attachments to account request timed out.'
          );
        }

        if (callback) {
          callback(null, response);
        }
      } catch (e) {
        const message = JSON.parse(e.message);
        if (callback) {
          callback(message);
        }
      }
    }
  };

const watchUploadTermsDocument = function* watchUploadTermsDocument() {
  while (true) {
    const { accountId, termsDocument, callback } = yield take(
      'users.uploadTermsDocument'
    );
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.uploadTermsDocument, accountId, termsDocument),
        timeout: delay(60000),
      });
      if (timeout) {
        throw new api.TimeoutException(
          'Upload terms document to account request timed out.'
        );
      }

      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      console.log(e);
      if (callback) {
        console.log(e);
        if (e.message instanceof Array) {
          callback(e.message[0].message.errors);
        } else {
          callback(e.message.errors);
        }
      }
    }
  }
};

const watchRemoveTermsDocument = function* watchRemoveTermsDocument() {
  while (true) {
    const { accountId, termsDocumentId, callback } = yield take(
      'users.removeTermsDocument'
    );
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.removeTermsDocument, accountId, {
          id: termsDocumentId,
        }),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException(
          'Remove terms document from account request timed out.'
        );
      }

      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      console.log(e);
      if (callback) {
        console.log(e);
        if (e.message instanceof Array) {
          callback(e.message[0].message.errors);
        } else {
          callback(e.message.errors);
        }
      }
    }
  }
};

const watchConnectWithXero = function* watchConnectWithXero() {
  while (true) {
    const { code, callback } = yield take('users.connectWithXero');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.connectWithXero, code),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException('Connect with Xero request timed out.');
      }

      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      if (callback) {
        if (e.message instanceof Array) {
          callback(e.message[0].message.errors);
        } else {
          callback(e.message.errors);
        }
      }
    }
  }
};

const watchConnectWithGoogle = function* watchConnectWithGoogle() {
  while (true) {
    const { code, callback } = yield take('users.connectWithGoogle');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.connectWithGoogle, code),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException(
          'Connect with Google request timed out.'
        );
      }

      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      if (callback) {
        if (e.message instanceof Array) {
          callback(e.message[0].message.errors);
        } else {
          callback(e.message.message);
        }
      }
    }
  }
};

const watchSyncXeroContacts = function* watchSyncXeroContacts() {
  while (true) {
    const { callback } = yield take('users.importXeroContacts');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.importXeroContacts),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException('Sync Xero contacts request timed out.');
      }

      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      if (callback) {
        if (e.message instanceof Array) {
          callback(e.message[0].message.errors);
        } else {
          callback(e.message.errors);
        }
      }
    }
  }
};

const watchSyncXeroItems = function* watchSyncXeroItems() {
  while (true) {
    const { callback } = yield take('users.importXeroItems');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.importXeroItems),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException('Sync Xero items request timed out.');
      }

      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      if (callback) {
        if (e.message instanceof Array) {
          callback(e.message[0].message.errors);
        } else {
          callback(e.message.errors);
        }
      }
    }
  }
};

const watchRemoveXeroConnection = function* watchRemoveXeroConnection() {
  while (true) {
    const { callback } = yield take('users.removeXeroConnection');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.removeXeroConnection),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException(
          'Remove Xero connection request timed out.'
        );
      }

      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      if (callback) {
        if (e.message instanceof Array) {
          callback(e.message[0].message.errors);
        } else {
          callback(e.message.errors);
        }
      }
    }
  }
};

const watchGetXeroAccounts = function* watchGetXeroAccounts() {
  while (true) {
    const { callback } = yield take('users.getXeroAccounts');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.getXeroAccounts),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException('Get Xero accounts request timed out.');
      }
      yield put(actions.storeXeroAccounts(response));

      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      if (callback) {
        if (e.message instanceof Array) {
          callback(e.message[0].message.errors);
        } else {
          callback(e.message.errors);
        }
      }
    }
  }
};

const watchUpdateXeroSettings = function* watchUpdateXeroSettings() {
  while (true) {
    const { payload, callback } = yield take('users.updateXeroSettings');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.updateXeroSettings, payload),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException(
          'Update Xero settings request timed out.'
        );
      }

      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      if (callback) {
        if (e.message instanceof Array) {
          callback(e.message[0].message.errors);
        } else {
          callback(e.message.errors);
        }
      }
    }
  }
};

const watchConnectWithQuickbooks = function* watchConnectWithQuickbooks() {
  while (true) {
    const { payload, callback } = yield take('users.connectWithQuickbooks');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.connectWithQuickbooks, payload),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException(
          'Connect with Quickbooks request timed out.'
        );
      }

      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      if (callback) {
        if (e.message instanceof Array) {
          callback(e.message[0].message.errors);
        } else {
          callback(e.message.errors);
        }
      }
    }
  }
};

const watchSyncQuickbooksCustomers = function* watchSyncQuickbooksCustomers() {
  while (true) {
    const { callback } = yield take('users.importQuickbooksCustomers');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.importQuickbooksCustomers),
        timeout: delay(45000),
      });
      if (timeout) {
        throw new api.TimeoutException(
          'Sync Quickbooks contacts request timed out.'
        );
      }

      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      if (callback) {
        if (e.message instanceof Array) {
          callback(e.message[0].message.errors);
        } else {
          callback(e.message.errors);
        }
      }
    }
  }
};

const watchSyncQuickbooksItems = function* watchSyncQuickbooksItems() {
  while (true) {
    const { callback } = yield take('users.importQuickbooksItems');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.importQuickbooksItems),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException(
          'Sync Quickbooks items request timed out.'
        );
      }

      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      if (callback) {
        if (e.message instanceof Array) {
          callback(e.message[0].message.errors);
        } else {
          callback(e.message.errors);
        }
      }
    }
  }
};

const watchRemoveQuickbooksConnection =
  function* watchRemoveQuickbooksConnection() {
    while (true) {
      const { callback } = yield take('users.removeQuickbooksConnection');
      yield call(refreshAuthToken);
      try {
        const { response, timeout } = yield race({
          response: call(api.removeQuickbooksConnection),
          timeout: delay(15000),
        });
        if (timeout) {
          throw new api.TimeoutException(
            'Remove Quickbooks connection request timed out.'
          );
        }

        if (callback) {
          callback(null, response);
        }
      } catch (e) {
        if (callback) {
          if (e.message instanceof Array) {
            callback(e.message[0].message.errors);
          } else {
            callback(e.message.errors);
          }
        }
      }
    }
  };

const watchGetAccountUsers = function* watchGetAccountUsers() {
  while (true) {
    const { callback } = yield take('users.getAccountUsers');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.getAccountUsers),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException('Get account users request timed out.');
      }
      yield put(actions.storeAccountUsers(response));
      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      yield put(actions.storeAccountUsers([]));
      console.log('Get account users error: ', e);
      if (callback) {
        callback(e);
      }
    }
  }
};

const watchReactivateUser = function* watchReactivateUser() {
  while (true) {
    const { userId, callback } = yield take('users.reactivateUser');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.reactivateUser, userId),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException('Reactivate user request timed out.');
      }
      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      console.log('Reactivate user error: ', e);
      if (callback) {
        callback(e);
      }
    }
  }
};

const watchInviteUserToAccount = function* watchInviteUserToAccount() {
  while (true) {
    const { payload, callback } = yield take('users.inviteUserToAccount');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.inviteUserToAccount, payload),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException(
          'Invite user to account request timed out.'
        );
      }
      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      console.log('Invite user to account error: ', e);
      if (callback) {
        callback(e);
      }
    }
  }
};

const watchDeleteUserFromAccount = function* watchDeleteUserFromAccount() {
  while (true) {
    const { userId, callback } = yield take('users.deleteUserFromAccount');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.deleteUserFromAccount, userId),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException(
          'Delete user from account request timed out.'
        );
      }
      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      console.log('Delete user from account error: ', e);
      if (callback) {
        callback(e);
      }
    }
  }
};

function* watchDeleteUsersFromAccount() {
  while (true) {
    const { userIds, callback } = yield take('users.deleteUsersFromAccount');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.deleteUsersFromAccount, userIds),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException(
          'Delete users from account request timed out.'
        );
      }
      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      console.log('Delete users from account error: ', e);
      if (callback) {
        callback(e);
      }
    }
  }
}

const watchConnectWithStripe = function* watchConnectWithStripe() {
  while (true) {
    const { code, callback } = yield take('users.connectWithStripe');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.connectWithStripe, code),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException(
          'Connect with Stripe request timed out.'
        );
      }

      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      if (callback) {
        if (e.message instanceof Array) {
          callback(e.message[0].message.errors);
        } else {
          callback(e.message.errors);
        }
      }
    }
  }
};

const watchRemoveStripeConnection = function* watchRemoveStripeConnection() {
  while (true) {
    const { callback } = yield take('users.removeStripeConnection');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.removeStripeConnection),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException(
          'Remove Stripe connection request timed out.'
        );
      }

      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      if (callback) {
        if (e.message instanceof Array) {
          callback(e.message[0].message.errors);
        } else {
          callback(e.message.errors);
        }
      }
    }
  }
};

const watchRemoveGoogleConnection = function* watchRemoveGoogleConnection() {
  while (true) {
    const { callback } = yield take('users.removeGoogleConnection');
    yield call(refreshAuthToken);
    try {
      const { response, timeout } = yield race({
        response: call(api.removeGoogleConnection),
        timeout: delay(15000),
      });
      if (timeout) {
        throw new api.TimeoutException(
          'Remove Google connection request timed out.'
        );
      }

      if (callback) {
        callback(null, response);
      }
    } catch (e) {
      if (callback) {
        if (e.message instanceof Array) {
          callback(e.message[0].message.errors);
        } else {
          callback(e.message.errors);
        }
      }
    }
  }
};

const watchLogout = function* watchLogout() {
  while (true) {
    const { callback } = yield take('auth.logout');
    yield put(actions.clearMyProfile());
  }
};

export const saga = function* saga() {
  yield all([
    watchReactivateUser(),
    watchLogout(),
    watchGetGlobalSettings(),
    watchRegisterUserDevice(),
    watchGetProfile(),
    watchUpdateNotificationSettings(),
    watchUpdatePassword(),
    watchResendEmailVerification(),
    watchValidateEmail(),
    watchValidatePhone(),
    watchCreateBusinessAccount(),
    watchUpdateBusinessAccount(),
    watchUploadAccountLogo(),
    watchGetAccountTerms(),
    watchManageTermsDocuments(),
    watchUploadTermsDocument(),
    watchRemoveTermsDocument(),
    watchConnectWithXero(),
    watchConnectWithGoogle(),
    watchSyncXeroContacts(),
    watchSyncXeroItems(),
    watchGetXeroAccounts(),
    watchRemoveXeroConnection(),
    watchConnectWithQuickbooks(),
    watchSyncQuickbooksCustomers(),
    watchSyncQuickbooksItems(),
    watchRemoveQuickbooksConnection(),
    watchUpdateXeroSettings(),
    watchGetAccountUsers(),
    watchInviteUserToAccount(),
    watchDeleteUserFromAccount(),
    watchDeleteUsersFromAccount(),
    watchConnectWithStripe(),
    watchRemoveStripeConnection(),
    watchRemoveGoogleConnection(),
    watchGetBusinessAttachments(),
    watchManageBusinessAttachments(),
  ]);
};
